import { memo, useEffect } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { ErrorCode, useDropzone } from 'react-dropzone';
import { useQueryClient } from '@zg-rentals/react-query5';
import { logError } from '@zg-rentals/log-error';
import { useRentalManagerUserPropertyPhotoWebApiHandler_upload } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { Toast, useToast } from '@zillow/constellation-10';

import { createZgApiHttpClient } from '../../../client/zgApiHttpClient';

import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import PropertiesActions from '../../actions/PropertiesActions';

import photoUtils from '../../utils/photoUtils';

import { GET_LISTING_RECS } from '../../constants/RecommendationsConstants';

import Photo from '../../model/Photo';

import UploadDropzone from '../../components/UploadDropzone';

import type { Dispatch, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';
import type { DatarouterError, UserPropertyPhotoUploadResponseWebDto } from '@zg-rentals/rental-manager-env';

const uploadingCountMap: Record<string, number> = {};

type Props = {
  isSmallDropzone?: boolean;
  setUploadingCount: Dispatch<SetStateAction<number>>;
  uploadingCount: number;
};

const PhotoUpload = ({ isSmallDropzone = false, setUploadingCount, uploadingCount }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const { photos, isSelfServeOnboardingInProgress, isUpdateRestricted, zgApiPublic } = useSelector((state) => {
    return {
      photos: state.properties.currentProperty.photos,
      isSelfServeOnboardingInProgress:
        state.properties.currentProperty.selfServeOnboardingStatus.isSelfServeOnboardingInProgress,
      isUpdateRestricted: state.properties.currentProperty.selfServeOnboardingStatus.isUpdateRestricted,
      zgApiPublic: state.env.zgApiPublic,
    };
  }, shallowEqual);

  // 3 minute timeout for photo uploads
  const { mutateAsync: uploadPhotoToDB } = useRentalManagerUserPropertyPhotoWebApiHandler_upload({
    httpClient: createZgApiHttpClient(zgApiPublic, { timeout: 3 * 60 * 1000 }),
  });

  useEffect(() => {
    if (uploadingCountMap[listingId] && Number(uploadingCountMap[listingId] || 0) !== uploadingCount) {
      setUploadingCount(uploadingCountMap[listingId]);
    }

    if (!uploadingCountMap[listingId] && uploadingCount !== 0) {
      setUploadingCount(0);
    }
  }, [uploadingCountMap[listingId]]);

  const onDropAccepted = async (acceptedFiles: Array<Blob>) => {
    const promiseArr: Array<Promise<UserPropertyPhotoUploadResponseWebDto>> = [];
    let errorArr: Array<string | undefined> = [];

    const acceptedFilesWithoutHEIC = photoUtils.filterForHEIC(acceptedFiles, onDropRejected);

    uploadingCountMap[listingId] = acceptedFilesWithoutHEIC.length;
    setUploadingCount(acceptedFilesWithoutHEIC.length);

    return photoUtils.preloadImages(acceptedFilesWithoutHEIC).then((preloadedFiles) => {
      (preloadedFiles as Array<Blob>)?.forEach((file) => {
        promiseArr.push(
          uploadPhotoToDB({
            getParams: { listingAlias: listingId },
            postData: { sourceFile: file },
          }),
        );
      });

      if (promiseArr.length > 0) {
        return Promise.allSettled(promiseArr)
          .then((response) => {
            // filter out error objects from response array
            // appear when user uploads photos in offline / unstable network mode (user sees blank photo placeholder)
            const photoArray = (
              response as unknown as Array<{
                error: unknown;
                url?: string;
                status: 'fulfilled' | 'rejected';
                reason?: DatarouterError;
                value: PhotoProps;
              }>
            )
              .filter((responseObject) => {
                const foundError =
                  (responseObject.error && responseObject.url === undefined) || responseObject.status === 'rejected';

                if (foundError) {
                  errorArr.push(responseObject?.reason?.message || 'There was an error uploading your photo');
                }

                return !foundError;
              })
              .map((successfulPhoto) => Photo(successfulPhoto.value));

            dispatch(
              PropertiesActions.updateCurrentProperty({
                photos: [...photos, ...photoArray],
              }),
            );
            queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });

            if (errorArr.filter((err) => err !== undefined).length > 0) {
              throw new Error();
            }

            return photoArray;
          })
          .catch(() => {
            [new Set(errorArr)].forEach((err) => {
              logError({ error: err, context: 'uploadPhoto' });
              enqueueToast(<Toast tone="critical">{err} </Toast>);
            });
          })
          .finally(() => {
            delete uploadingCountMap[listingId];

            errorArr = [];
          });
      }

      return;
    });
  };

  const onDropRejected = (errorData?: Array<{ errors?: Array<{ message: string; code?: string }> }>) => {
    const error = errorData?.[0]?.errors?.[0];
    enqueueToast(
      <Toast tone="critical">
        {error?.code === ErrorCode.FileInvalidType ? 'File type must be an image' : error?.message}
      </Toast>,
    );
  };

  const isUploadDisabled = uploadingCount > 0 || (isSelfServeOnboardingInProgress && isUpdateRestricted);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject, acceptedFiles } = useDropzone({
    accept: 'image/*',
    onDropAccepted,
    onDropRejected,
    disabled: isUploadDisabled,
  });

  const getRootPropsWithTabIndex = () => ({
    ...getRootProps({ isFocused, isDragAccept, isDragReject, acceptedFiles }),
    tabIndex: isSmallDropzone ? 0 : -1,
  });

  return (
    <UploadDropzone
      isSmallDropzone={isSmallDropzone}
      isDisabled={isUploadDisabled}
      isEmpty={photos.length === 0 && uploadingCount === 0}
      rootProps={getRootPropsWithTabIndex()}
      inputProps={getInputProps()}
    />
  );
};

export default memo(PhotoUpload);
